import React from 'react'
import { isEmpty } from 'lodash'
import pluralize from 'pluralize'
import { Pill } from '@/components/pills/Pill'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { VOIP_CAMPAIGN_FILTERS } from './constants'

export const getFormSchema = (dropdownOptions, isUnmatchedEdit = false) => {
  const schema = []

  if (!isUnmatchedEdit) {
    schema.push({
      name: 'name',
      type: 'text',
      label: 'VoIP Campaign Name',
      required: true,
      placeholder: 'Enter campaign name...',
      maxLength: 100,
    })
  }

  schema.push({
    name: 'config_cid',
    type: 'select',
    label: 'Playbook',
    options: dropdownOptions,
    required: true,
    placeholder: 'Select playbook...',
    search: true,
  })

  return schema
}

export const formatMappedCampaigns = (mappedCampaigns, configs) => {
  return (mappedCampaigns || []).map((campaign) => {
    const [campaignConfig] = (configs || []).filter((config) => config.cid === campaign.config_cid)
    return {
      voipCampaignName: campaign?.name || 'Missing Campaign Name',
      campaignConfigName: campaignConfig?.name || 'Deleted Playbook',
      originalCampaign: campaign,
    }
  })
}

export const formatUnmappedCampaigns = (unmappedCampaigns) => {
  return (unmappedCampaigns || []).map((name) => ({
    voipCampaignName: name,
    campaignConfigName: (
      <InteractiveTooltip content="Unmapped campaigns will default to the user's assigned playbook.">
        <Pill key={name} small critical>
          Unmapped
        </Pill>
      </InteractiveTooltip>
    ),
    originalCampaign: {
      name,
      isUnmatched: true,
    },
  }))
}

export const getTableData = (
  activeFilter,
  formattedMappedCampaigns,
  formattedUnmappedCampaigns
) => {
  const formattedAllCampaigns = [...formattedMappedCampaigns, ...formattedUnmappedCampaigns]

  switch (activeFilter) {
    case VOIP_CAMPAIGN_FILTERS.MAPPED:
      return formattedMappedCampaigns
    case VOIP_CAMPAIGN_FILTERS.UNMAPPED:
      return formattedUnmappedCampaigns
    case VOIP_CAMPAIGN_FILTERS.ALL:
    default:
      return formattedAllCampaigns
  }
}

export const createDropdownOptions = (configs, selectedOrganizationId) => {
  return (configs || [])
    .filter((config) => Number(config.organization_id) === Number(selectedOrganizationId))
    .map((config) => ({
      value: config.cid,
      label: config.name,
    }))
}

export const createDeleteConfirmationContent = (campaigns, affectedScorecards) => {
  const isSingle = campaigns.length === 1

  if (isEmpty(affectedScorecards)) {
    return `Are you sure you want to delete ${isSingle ? 'this' : 'these'} ${pluralize('VoIP campaign', campaigns.length, true)}? This action cannot be undone.`
  }

  return (
    <label data-testid="delete-campaign-warning">
      {`${isSingle ? 'This' : 'These'} ${pluralize('VoIP campaign', campaigns.length, true)} ${
        isSingle ? 'is' : 'are'
      } currently used in the eligibility settings for the following ${pluralize(
        'scorecard',
        affectedScorecards.length,
        true
      )}: `}
      {affectedScorecards.map((scorecard, idx) => (
        <span key={scorecard.id}>
          <a target="_blank" rel="noopener noreferrer" href={`/qa-copilot/${scorecard.id}`}>
            {scorecard.name}
          </a>
          {idx < affectedScorecards.length - 1 ? ', ' : ' . '}
        </span>
      ))}
      {`Deleting ${isSingle ? 'it' : 'them'} will also remove ${isSingle ? 'it' : 'them'} from ${pluralize(
        'scorecard',
        affectedScorecards.length,
        true
      )}, which may impact qualifying calls for scoring.`}
    </label>
  )
}
